/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import * as Sentry from "@sentry/react";

type Error = {
  message: string;
  stack: string;
  context?: string;
  name: string;
  originalError: PotentialErrors;
};

const isPromise = (val: any): boolean =>
  (!!val &&
    typeof val === "object" &&
    typeof val.then === "function" &&
    typeof val.catch === "function") ||
  (!!val && typeof val === "object" && "reason" in val && "promise" in val);

const isResponseLike = (val: any): boolean =>
  !!val &&
  typeof val === "object" &&
  "status" in val &&
  "ok" in val &&
  typeof (val as any).status === "number";

const safeStringify = (value: any): string => {
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (_key, val) => {
        if (typeof val === "object" && val !== null) {
          if (seen.has(val)) return "[Circular]";
          seen.add(val);

          if (val instanceof Error) {
            return {
              name: val.name,
              message: val.message,
              stack: val.stack,
              ...((val as any).code ? { code: (val as any).code } : {}),
            };
          }

          // Tolerate Response-like objects
          if (isResponseLike(val)) {
            return {
              status: (val as any).status,
              statusText: (val as any).statusText,
              url: (val as any).url,
              ok: (val as any).ok,
              type: (val as any).type,
            };
          }
        }

        if (typeof val === "function")
          return `[Function ${val.name || "anonymous"}]`;

        if (isPromise(val)) return "[Promise]";
        return val;
      },
      2,
    );
  } catch {
    return String(value);
  }
};

const extractMessage = (err: any): string => {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.message) return String(err.message);
  if (err.error) return String(err.error);
  if (err.statusText) return String(err.statusText);
  return safeStringify(err);
};

/**
 * Captures an error to Sentry with consistent formatting
 * - Always passes an Error instance to Sentry
 * - Adds formatted message and useful extras
 */

type PotentialErrors =
  | PromiseRejectedResult
  | Response
  | Record<string, any>
  | string;

const reformat = (error: PotentialErrors, context?: string): Error => {
  const stack = new Error().stack;
  // if (isPromise(error)) {
  //   return {
  //     message: "Promise Rejected Error",
  //     stack: stack ?? "unknown",
  //     name: "Promise Rejected Error",
  //     originalError: error,
  //     context,
  //   };
  // }
  // if ((error as DOMException)?.name === "AbortError") {
  //   return {
  //     message: "Abort Error - The request was aborted",
  //     stack: stack ?? "unknown",
  //     name: "Abort Error",
  //     originalError: error,
  //     context,
  //   };
  // }
  if (isResponseLike(error)) {
    const res = error as Response;
    return {
      message: `API Error ${safeStringify(res.url ?? "unknown url")}: ${safeStringify(
        res.status ?? "unknown status",
      )} - ${safeStringify(
        res.statusText ?? "unknown status text",
      )} - ${extractMessage(error)}`,
      stack: stack ?? "unknown",
      name:
        context ??
        `API Error - ${safeStringify(res.status ?? "unknown status")}`,
      originalError: error,
      context,
    };
  }

  if (error instanceof Error) {
    return {
      message: `Exception - ${safeStringify(error.name ?? "unknown name")} - ${safeStringify(error.message ?? "unknown message")}`,
      originalError: error,
      context,
      stack: error.stack ?? "unknown",
      name:
        context ??
        `Application code error - ${safeStringify(error.name ?? "unknown name")}`,
    };
  }

  // String
  if (typeof error === "string") {
    return {
      message: `Exception - String Error - ${safeStringify(error ?? "unknown error")}`,
      originalError: error,
      context,
      name:
        context ??
        `Application code error - ${safeStringify(error ?? "unknown error")}`,
      stack: stack ?? "unknown",
    };
  }

  return {
    message: `Exception - Unknown Error - ${safeStringify(error ?? "unknown error")}`,
    originalError: error,
    context,
    name: context ?? "Unknown Error",
    stack: stack ?? "unknown",
  };
};

export const sentryCaptureError = async (error: any, context?: string) => {
  const formatted = reformat(error, context);
  if (context) {
    Sentry.setContext("error_context", { location: context });
  }

  Sentry.setTag("error_type", formatted.name);
  // if (formatted.statusCode)
  //   Sentry.setTag("http.status_code", String(formatted.statusCode));
  // if (formatted.method) Sentry.setTag("http.method", String(formatted.method));
  // if (formatted.url) Sentry.setTag("http.url", String(formatted.url));

  const toCapture = new Error(formatted.message, {
    cause: formatted,
  });

  toCapture.name = formatted.name;

  Sentry.captureException(toCapture, {
    extra: {
      ...formatted,
    },
  });
};
